import * as React from "react";
import {useDispatch} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MusicOffIcon from '@mui/icons-material/MusicOff';
import {DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent} from "@dnd-kit/core";
import {SortableContext, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {usePlaylistDispatch, usePlaylistSelector} from "../../store/slices/playlist/playlistSlice";
import {PlaylistItem} from "./components/playlistItem/PlaylistItem";
import {SortablePlaylistItem} from "./components/playlistItem/SortablePlaylistItem";
import {PlaylistSkeleton} from "./components/playlistSkeleton/PlaylistSkeleton";
import {usePlayerDispatch, usePlayerSelector} from "../../store/slices/player/playerSlice";
import {updateNowPlaying} from "../../store/slices/player/actions";
import {Playlist as PlaylistType, Song} from "../../store/api/types";
import {PlaylistHeader} from "./components/playlistHeader/PlaylistHeader";
import {getPlaylist} from "../../store/slices/playlist/actions";
import {useUserSelector} from "../../store/slices/user/userSlice";
import {deletePlaylist, renamePlaylist, removeTrack, reorderTracks} from "../../store/slices/userPlaylists/reducers";
import {toggleFavorite} from "../../store/slices/favorites/reducers";
import {useIsFavorite} from "../../store/slices/favorites/favoritesSlice";
import {useSharedPlaylist} from "../../hooks/useSharedPlaylist";
import {LIKED_SONGS_ID, useLikedSongsPlaylist} from "../../store/slices/likedSongs/likedSongsSlice";
import {removeLikedSong} from "../../store/slices/likedSongs/reducers";
import {useDocumentTitle} from "../../hooks/useDocumentTitle";
import {Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Tooltip} from "@mui/material";
import {CreatePlaylistDialog} from "../library/CreatePlaylistDialog";
import {PlaylistShareControls} from "./components/playlistShareControls/PlaylistShareControls";
import {PlaylistShareButton} from "./components/playlistShareButton/PlaylistShareButton";
import {EmptyState} from "../common/emptyState/EmptyState";
import styles from './Playlist.module.scss';

export const Playlist: React.FC = () => {
    const {isLoading} = usePlaylistSelector(['isLoading']);
    const {nowPlaying} = usePlayerSelector(['nowPlaying']);
    const {recentlyPlayed} = useUserSelector(['recentlyPlayed']);
    const playerDispatch = usePlayerDispatch();
    const playlistDispatch = usePlaylistDispatch();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {playlistId} = useParams();
    const isLikedSongsView = playlistId === LIKED_SONGS_ID;
    const isUserPlaylist = playlistId?.startsWith('user-') ?? false;
    const shared = useSharedPlaylist(isUserPlaylist && !isLikedSongsView ? playlistId : undefined);
    const userPlaylist = shared.playlist;
    const canEdit = isUserPlaylist && !isLikedSongsView && shared.isOwner;
    const isFavorite = useIsFavorite(playlistId ?? '');
    const likedSongsPlaylist = useLikedSongsPlaylist();

    const [renameOpen, setRenameOpen] = React.useState(false);
    const [deleteOpen, setDeleteOpen] = React.useState(false);

    React.useEffect(() => {
        if (playlistId && !isUserPlaylist) {
            playlistDispatch(getPlaylist(playlistId));
        }
    }, [playlistDispatch, playlistId, isUserPlaylist]);

    const playlist: PlaylistType | undefined = React.useMemo(() => {
        if (isLikedSongsView) {
            return likedSongsPlaylist;
        }
        if (isUserPlaylist && userPlaylist) {
            return {id: userPlaylist.id, name: userPlaylist.name, artist: `${userPlaylist.tracks.length} songs`, img: userPlaylist.img, tracks: userPlaylist.tracks};
        }
        return playlistId ? recentlyPlayed?.[playlistId] : undefined;
    }, [isLikedSongsView, likedSongsPlaylist, isUserPlaylist, userPlaylist, recentlyPlayed, playlistId]);

    useDocumentTitle(playlist?.name ?? 'Playlist');

    const handlePlaylistItemClicked = (song: Song) => {
        playerDispatch(updateNowPlaying({nowPlaying: {song, playlist: playlist!}, isInitiatedByPlayer: false, source: 'playlist'}));
    };

    const handlePlayPlaylist = () => {
        if (playlist?.tracks?.length) {
            handlePlaylistItemClicked(playlist.tracks[0]);
        }
    };

    const handleToggleFavorite = () => {
        if (playlist) {
            dispatch(toggleFavorite(playlist));
        }
    };

    const handleDeleteConfirm = () => {
        if (playlistId) {
            dispatch(deletePlaylist(playlistId));
            navigate('/library');
        }
        setDeleteOpen(false);
    };

    const handleRename = (name: string) => {
        if (playlistId) {
            dispatch(renamePlaylist({id: playlistId, name}));
        }
        setRenameOpen(false);
    };

    const handleRemoveTrack = (songId: string) => {
        if (isLikedSongsView) {
            dispatch(removeLikedSong(songId));
            return;
        }
        if (playlistId) {
            dispatch(removeTrack({playlistId, songId}));
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(TouchSensor, {activationConstraint: {delay: 200, tolerance: 5}}),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;
        if (!over || active.id === over.id || !playlistId || !playlist?.tracks) return;
        const fromIndex = playlist.tracks.findIndex(t => t.id === active.id);
        const toIndex = playlist.tracks.findIndex(t => t.id === over.id);
        if (fromIndex !== -1 && toIndex !== -1) {
            dispatch(reorderTracks({playlistId, fromIndex, toIndex}));
        }
    };

    if ((!isUserPlaylist && !isLikedSongsView && isLoading && !playlist) || (isUserPlaylist && !isLikedSongsView && shared.loading && !playlist)) {
        return <PlaylistSkeleton />;
    }

    if (!playlist) {
        return (
            <EmptyState
                icon={<MusicOffIcon/>}
                title="Playlist unavailable"
                description={
                    isUserPlaylist
                        ? "It may be private, have been deleted, or the link is broken."
                        : "We couldn't load this playlist. Try again in a moment."
                }
            />
        );
    }

    return (
        <div>
            <PlaylistHeader playlist={playlist}>
                {canEdit && (
                    <>
                        <IconButton onClick={() => setRenameOpen(true)} size="small" sx={{color: 'white'}}>
                            <EditIcon/>
                        </IconButton>
                        <IconButton onClick={() => setDeleteOpen(true)} size="small" sx={{color: 'white'}}>
                            <DeleteIcon/>
                        </IconButton>
                    </>
                )}
            </PlaylistHeader>
            <div className={styles.playlist__actions}>
                <Tooltip title="Play playlist">
                    <span>
                        <IconButton onClick={handlePlayPlaylist} className={styles.playlist__play} disabled={!playlist?.tracks?.length} aria-label="Play playlist">
                            <PlayArrowIcon/>
                        </IconButton>
                    </span>
                </Tooltip>
                {!isUserPlaylist && (
                    <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                        <IconButton
                            onClick={handleToggleFavorite}
                            className={isFavorite ? styles.playlist__liked : styles.playlist__like}
                            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                            {isFavorite ? <StarIcon/> : <StarBorderIcon/>}
                        </IconButton>
                    </Tooltip>
                )}
                {!isUserPlaylist && !isLikedSongsView && playlist && (
                    <PlaylistShareButton
                        playlistId={playlist.id}
                        playlistName={playlist.name}
                        playlistType="api"
                    />
                )}
                {canEdit && userPlaylist && (
                    <PlaylistShareControls
                        playlistId={userPlaylist.id}
                        playlistName={userPlaylist.name}
                        visibility={userPlaylist.visibility}
                    />
                )}
            </div>
            {canEdit ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={(playlist?.tracks || []).map(t => t.id)} strategy={verticalListSortingStrategy}>
                        <List className={styles.playlist__list} sx={{width: '100%'}}>
                            {(playlist?.tracks || []).map((track, index) =>
                                <SortablePlaylistItem
                                    key={track.id}
                                    id={track.id}
                                    item={track}
                                    index={index + 1}
                                    onClick={handlePlaylistItemClicked}
                                    selected={nowPlaying?.playlist?.id === playlist?.id && nowPlaying?.song?.id === track?.id}
                                    onRemove={() => handleRemoveTrack(track.id)}
                                    sourceImg={playlist?.img}
                                />)}
                        </List>
                    </SortableContext>
                </DndContext>
            ) : (
                <List className={styles.playlist__list} sx={{width: '100%'}}>
                    {(playlist?.tracks || []).map((track, index) =>
                        <PlaylistItem
                            key={track.id}
                            item={track}
                            index={index + 1}
                            onClick={handlePlaylistItemClicked}
                            selected={nowPlaying?.playlist?.id === playlist?.id && nowPlaying?.song?.id === track?.id}
                            onRemove={isLikedSongsView ? () => handleRemoveTrack(track.id) : undefined}
                            sourceImg={playlist?.img}
                        />)}
                </List>
            )}

            <CreatePlaylistDialog
                open={renameOpen}
                onClose={() => setRenameOpen(false)}
                onConfirm={handleRename}
                initialName={userPlaylist?.name ?? ''}
                title="Rename Playlist"
            />

            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                <DialogTitle>Delete Playlist</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{playlist?.name}"? This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
